import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PatientControllerApi } from '../../api/generated';
import type {
  InterventionGroupDto,
  PatientResponse,
  TreatmentPlanDto,
  WeekDateRangeResponse
} from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectPatientsById, setPatients } from '../../store/patients/patientsSlice';
import { selectRoomPropertiesLoaded } from '../../store/roomProperties/roomPropertiesSlice';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import SearchIcon from '../../assets/icons/search.svg?react';
import { InterventionIconComponent } from '../InterventionIcon/InterventionIcon';
import { PatientDetailsCard } from '../PatientDetails/PatientDetailsCard';
import type { PatientCreatePrefill } from '../PatientDetails/types/patientDetailsCard.types';
import { AuditLog } from '../PatientDetails/AuditLog';
import { isCprPrefix, stripNonDigits } from '../../utils/stringUtils';
import { useAuditLog } from '../../hooks/useAuditLog';
import { AffectedGroupsPicker } from './AffectedGroupsPicker';
import { computeAffectedGroupIds, DEFAULT_AFFECTED_GROUPS_CHOICE } from './affectedGroups';
import { Button } from '../UI/base/Button';
import { LoadError } from '../UI/LoadError';
import { LoadingPlaceholder } from '../UI/LoadingPlaceholder';
import { useHasBeenTrue } from '../../hooks/useHasBeenTrue';
import { INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS } from './interventionGroupDrawerConstants';
import type { TimelineItem } from './types';
import styles from './PatientDetailView.module.css';

export interface PatientDetailViewHandle {
  // Try to handle a back action internally. Returns synchronously, before the
  // slide-out animation completes:
  //   true  → view triggered the slide from the inline edit form back to the
  //           read-only details view.
  //   false → nothing for this view to undo (read-only details, or the
  //           "add patient" flow which has no underlying details view); the
  //           parent should close the patient view.
  goBack: () => boolean;
  // Returns true if the add-patient flow has any state that would be lost on close.
  isAddDirty: () => boolean;
}

interface PatientDetailViewProps {
  patient: PatientResponse;
  treatmentPlanName: string;
  weekRange: string;
  timelineItems: TimelineItem[];
  weeks: WeekDateRangeResponse[];
  strokeColor: string;
  fillColor: string;
  initialEditing?: boolean;
  locationId?: string;
  plan?: TreatmentPlanDto;
  selectedGroup?: InterventionGroupDto;
  onAssignPatient?: (patientId: string, groupIds: string[], justCreated: boolean) => Promise<void>;
  assignedPatientIds?: string[];
  // Owned by the drawer, which does the fetching; surfaced here because the
  // edit form is the only thing that waits on the table.
  hasRoomPropertiesError?: boolean;
  onRetryRoomProperties?: () => void;
  ref?: React.Ref<PatientDetailViewHandle>;
}

export const PatientDetailView = ({
  patient,
  treatmentPlanName,
  weekRange,
  timelineItems,
  weeks,
  strokeColor,
  fillColor,
  initialEditing = false,
  locationId,
  plan,
  selectedGroup,
  onAssignPatient,
  assignedPatientIds = [],
  hasRoomPropertiesError = false,
  onRetryRoomProperties,
  ref
}: PatientDetailViewProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const patientsById = useAppSelector(selectPatientsById);
  // The drawer owns the fetch and hands down its failure/retry; this component
  // only reads the resulting flag, so there is no competing fetch path.
  // Latched: a later `clearRoomProperties` (a year change resolving to no
  // scenario) must not unmount an edit in progress and discard typed input.
  const canEdit = useHasBeenTrue(useAppSelector(selectRoomPropertiesLoaded));
  const isNew = initialEditing;

  const { data: auditLogEntries } = useAuditLog(locationId, patient.id);

  // Existing-patient view ↔ edit slide animation.
  const [isEditing, setIsEditing] = useState(false);
  const [isEditSliding, setIsEditSliding] = useState(false);
  const editSlideOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add-patient flow state.
  const [nameQuery, setNameQuery] = useState('');
  const [showNameResults, setShowNameResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [pickedExisting, setPickedExisting] = useState<PatientResponse | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [pendingGroupIds, setPendingGroupIds] = useState<string[]>(() =>
    plan && selectedGroup
      ? computeAffectedGroupIds(plan, selectedGroup, DEFAULT_AFFECTED_GROUPS_CHOICE)
      : []
  );
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () => () => {
      if (editSlideOutTimerRef.current !== null) clearTimeout(editSlideOutTimerRef.current);
    },
    []
  );

  const refreshPatientsList = useCallback(async () => {
    if (!locationId) return;
    try {
      const config = createApiConfiguration();
      const patientApi = new PatientControllerApi(config);
      const listResponse = await patientApi.listPatients({ locationId });
      dispatch(setPatients(listResponse.data));
    } catch (refreshError) {
      console.error('Failed to refresh patient list:', refreshError);
    }
  }, [locationId, dispatch]);

  const handleEditStart = useCallback(() => {
    setIsEditing(true);
    requestAnimationFrame(() => setIsEditSliding(true));
  }, []);

  const handleEditEnd = useCallback(() => {
    setIsEditSliding(false);
    if (editSlideOutTimerRef.current !== null) clearTimeout(editSlideOutTimerRef.current);
    editSlideOutTimerRef.current = setTimeout(() => {
      setIsEditing(false);
      editSlideOutTimerRef.current = null;
    }, INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goBack: () => {
        if (isEditing && !isNew) {
          handleEditEnd();
          return true;
        }
        // Read-only details view, or the "add patient" flow which has no
        // underlying details view. Parent will close the patient view.
        return false;
      },
      isAddDirty: () =>
        isNew && (nameQuery.length > 0 || pickedExisting !== null || isCreatingNew)
    }),
    [isEditing, isNew, handleEditEnd, nameQuery, pickedExisting, isCreatingNew]
  );

  // ---------------- Add-patient flow ----------------

  const assignedSet = useMemo(() => new Set(assignedPatientIds), [assignedPatientIds]);

  const nameSearchResults = useMemo(() => {
    if (nameQuery.length < 2) return [];
    const query = nameQuery.toLowerCase();
    const cprQuery = stripNonDigits(nameQuery);
    return Object.values(patientsById).filter((p) => {
      const matchesName = p.name?.toLowerCase().includes(query) ?? false;
      const matchesCpr =
        cprQuery.length > 0 && stripNonDigits(p.cprNumber ?? '').includes(cprQuery);
      return (matchesName || matchesCpr) && (!p.id || !assignedSet.has(p.id));
    });
  }, [nameQuery, patientsById, assignedSet]);

  const dropdownItemCount = 1 + nameSearchResults.length;

  // A query that looks like the start of a Danish CPR (ddMMyy-XXXX) prefills the
  // CPR field, otherwise the name field.
  const createPrefill = useMemo<PatientCreatePrefill | undefined>(() => {
    const trimmed = nameQuery.trim();
    if (!trimmed) return undefined;
    return isCprPrefix(trimmed) ? { cpr: trimmed } : { name: trimmed };
  }, [nameQuery]);

  const resetSearch = useCallback(() => {
    setNameQuery('');
    setShowNameResults(false);
    setHighlightedIndex(-1);
    setPickedExisting(null);
    setIsCreatingNew(false);
  }, []);

  const handleNameInput = useCallback((value: string) => {
    setNameQuery(value);
    setShowNameResults(value.length >= 2);
    setHighlightedIndex(-1);
    setPickedExisting(null);
    setIsCreatingNew(false);
  }, []);

  const handleSelectExisting = useCallback((selected: PatientResponse) => {
    setPickedExisting(selected);
    setIsCreatingNew(false);
    setNameQuery(selected.name);
    setShowNameResults(false);
  }, []);

  const handleSelectCreateNew = useCallback(() => {
    setIsCreatingNew(true);
    setPickedExisting(null);
    setShowNameResults(false);
  }, []);

  const handlePatientCreated = (created: PatientResponse) => {
    if (!onAssignPatient || pendingGroupIds.length === 0) return;
    void onAssignPatient(created.id, pendingGroupIds, true);
  };

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showNameResults) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % dropdownItemCount);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev <= 0 ? dropdownItemCount - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex === 0) {
          handleSelectCreateNew();
        } else if (highlightedIndex > 0) {
          const selected = nameSearchResults[highlightedIndex - 1];
          if (selected) handleSelectExisting(selected);
        }
      } else if (e.key === 'Escape') {
        setShowNameResults(false);
        setHighlightedIndex(-1);
      }
    },
    [
      showNameResults,
      dropdownItemCount,
      highlightedIndex,
      nameSearchResults,
      handleSelectCreateNew,
      handleSelectExisting
    ]
  );

  const handleConfirmAssign = useCallback(async () => {
    if (!pickedExisting?.id || pendingGroupIds.length === 0 || !onAssignPatient) return;
    try {
      await onAssignPatient(pickedExisting.id, pendingGroupIds, false);
    } catch {
      // Defensive: parent's handleAssignPatient catches internally today, but
      // surface a fallback error here so a future refactor that lets the
      // rejection escape doesn't silently disappear via Button's auto-pending.
      dispatch(showError({ message: t('error-patient-not-assigned') }));
    }
  }, [pickedExisting, pendingGroupIds, onAssignPatient, dispatch, t]);

  // No-op required by PatientDetailsCard's onEditStart/onEditEnd contract for
  // the "picked existing" sub-flow: the user can edit the existing patient
  // before assigning, but the slide animation is owned by PatientDetailsCard
  // itself in that path (it just swaps view↔form internally). We don't need to
  // mirror it here.
  const noop = useCallback(() => {}, []);

  // ---------------- Treatment timeline (existing patient view) ----------------

  const currentWeek = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (const w of weeks) {
      if (!w.startDate || !w.endDate) continue;
      const start = new Date(w.startDate);
      const end = new Date(w.endDate);
      if (today >= start && today <= end) return w.weekNumber;
    }
    return null;
  }, [weeks]);

  const activeItemIndex = useMemo(() => {
    if (currentWeek == null) return -1;
    const idx = timelineItems.findIndex((item) => item.week === currentWeek);
    return idx >= 0 ? idx : -1;
  }, [currentWeek, timelineItems]);

  const treatmentTimelineCard = (
    <div className={styles.treatmentCard}>
      <h3 className={styles.treatmentTitle}>{t('active-treatment-plan')}</h3>
      <div className={styles.treatmentMeta}>
        <span className={styles.treatmentMetaLabel}>{t('active-treatment-plan-name')}:</span>{' '}
        {treatmentPlanName}
      </div>
      <div className={styles.treatmentMeta}>
        <span className={styles.treatmentMetaLabel}>{t('period-label')}:</span> {weekRange}
      </div>
      <div className={styles.timeline}>
        {timelineItems.map((item, index) => {
          const isActive = index === activeItemIndex;
          const colorStyle = isActive
            ? {
                backgroundColor: 'var(--color-interaction-tertiary)',
                border: '1px solid var(--color-border-focus)'
              }
            : { backgroundColor: fillColor, border: `1.5px solid ${strokeColor}` };

          return (
            <div key={item.id} className={styles.timelineItem}>
              <div className={styles.iconColumn}>
                {item.icon ? (
                  <div className={styles.iconCircle} style={colorStyle}>
                    <InterventionIconComponent icon={item.icon} />
                  </div>
                ) : (
                  <div className={styles.moduleBadge} style={colorStyle}>
                    {item.moduleLabel}
                  </div>
                )}
              </div>
              <div className={styles.itemContent}>
                <span className={styles.itemName}>{item.name}</span>
                <span className={styles.itemWeek}>
                  {t('week-label')} {item.week}
                  {isActive && (
                    <span className={styles.currentSessionPill}>{t('current-session')}</span>
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---------------- Render ----------------

  // Only the edit form waits on the room-properties table — see `canEdit`. The
  // patient search, the create form, the read-only card, the timeline and the
  // audit log never send room needs, and the field is optional, so a missing
  // table must not hide any of them.
  if (isNew) {
    if (!locationId) return null;

    const hasSelection = isCreatingNew || pickedExisting !== null;

    return (
      <div className={styles.container}>
        <div className={styles.nameSearchWrapper}>
          <span className={styles.fieldLabel}>{t('add-patient-name-or-cpr')}</span>
          <div className={styles.nameSearchInput}>
            <SearchIcon className={styles.searchIcon} />
            <input
              ref={nameInputRef}
              className={styles.searchField}
              type="text"
              placeholder={t('name-placeholder')}
              value={nameQuery}
              onChange={(e) => handleNameInput(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onFocus={() => {
                if (nameQuery.length >= 2) setShowNameResults(true);
              }}
              onBlur={() => {
                setTimeout(() => setShowNameResults(false), 200);
              }}
            />
            {hasSelection && (
              <button
                className={styles.pillRemove}
                onMouseDown={(e) => e.preventDefault()}
                onClick={resetSearch}
                aria-label={t('go-back')}
                type="button"
              >
                &times;
              </button>
            )}
          </div>
          {showNameResults && (
            <div className={styles.nameSearchDropdown}>
              <button
                className={`${styles.nameSearchItem} ${highlightedIndex === 0 ? styles.nameSearchItemHighlighted : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSelectCreateNew}
                type="button"
              >
                <span className={styles.createNewLabel}>
                  {isCprPrefix(nameQuery.trim())
                    ? t('create-new-with-cpr')
                    : t('create-new-with-name')}
                  {nameQuery && `: ${nameQuery}`}
                </span>
              </button>
              {nameSearchResults.map((p, i) => (
                <button
                  key={p.id}
                  className={`${styles.nameSearchItem} ${highlightedIndex === i + 1 ? styles.nameSearchItemHighlighted : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectExisting(p)}
                  type="button"
                >
                  <span className={styles.nameSearchName}>{p.name}</span>
                  <span className={styles.nameSearchCpr}>
                    {p.cprNumber
                      ? `${p.cprNumber.slice(0, 2)}/${p.cprNumber.slice(2, 4)}/${p.cprNumber.slice(4, 6)}`
                      : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {(isCreatingNew || pickedExisting) && plan && selectedGroup && (
          <div className={styles.assignSection}>
            <AffectedGroupsPicker
              plan={plan}
              selectedGroup={selectedGroup}
              onChange={setPendingGroupIds}
            />
          </div>
        )}

        {isCreatingNew && (
          <PatientDetailsCard
            locationId={locationId}
            mode="create"
            createPrefill={createPrefill}
            onEditStart={noop}
            onEditEnd={noop}
            onPatientUpdated={() => void refreshPatientsList()}
            onPatientCreated={handlePatientCreated}
          />
        )}

        {pickedExisting && (
          <>
            <PatientDetailsCard
              patient={pickedExisting}
              locationId={locationId}
              mode="view"
              onEditStart={noop}
              onEditEnd={noop}
              onPatientUpdated={() => void refreshPatientsList()}
            />
            <div className={styles.assignFooter}>
              <Button
                type="primary"
                onClick={handleConfirmAssign}
                disabled={pendingGroupIds.length === 0}
              >
                {t('add-patient')}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Existing-patient flow.
  if (!locationId) return null;

  return (
    <div className={styles.container}>
      <div className={styles.detailSlideContainer}>
        <div className={`${styles.detailView} ${isEditing ? styles.detailViewOut : ''}`}>
          <PatientDetailsCard
            patient={patient}
            locationId={locationId}
            mode="view"
            onEditStart={handleEditStart}
            onEditEnd={handleEditEnd}
            onPatientUpdated={() => void refreshPatientsList()}
          />

          {treatmentTimelineCard}

          <AuditLog entries={auditLogEntries} />
        </div>

        {isEditing && (
          <div className={`${styles.editView} ${isEditSliding ? styles.editViewIn : ''}`}>
            {/* The only card that can destroy room needs: seeded from an
                unloaded table it would send an empty list on save. */}
            {canEdit && (
              <PatientDetailsCard
                patient={patient}
                locationId={locationId}
                mode="edit"
                onEditStart={handleEditStart}
                onEditEnd={handleEditEnd}
                onPatientUpdated={() => void refreshPatientsList()}
              />
            )}
            {!canEdit && hasRoomPropertiesError && onRetryRoomProperties && (
              <LoadError
                message={t('room-properties-load-error')}
                onRetry={onRetryRoomProperties}
              />
            )}
            {!canEdit && !hasRoomPropertiesError && <LoadingPlaceholder />}
          </div>
        )}
      </div>
    </div>
  );
};
